import moment from "moment";
import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { apiGetPlanningEvents, apiGetPlanningPrestations, apiGetPlanningSites } from "../../api/ApiPlanning";
import { buildIndex, DATE_FORMAT, monthRange } from "./planningData";

/**
 * Active sites, with the last day they show as active (two months from today when they have no end date).
 */
export const usePlanningSites = () => {
  const sitesQuery = useQuery(apiGetPlanningSites());

  const sites = useMemo(() => {
    const defaultEnd = moment().add(2, "month").format(DATE_FORMAT);

    return (sitesQuery.data?.content || []).map(site => ({...site, activeEnd: site.dateEnd || defaultEnd}));
  }, [sitesQuery.data]);

  return {sitesQuery, sites};
};

/**
 * Events and prestations of the given date ranges ({from, to}), loaded range by range,
 * with their index per site and day. staleTime avoids loading again what a route loader has just loaded.
 */
export const usePlanningRanges = (ranges, enabled, staleTime = 0) => {
  const events = useQueries({
    queries: ranges.map(range => ({...apiGetPlanningEvents(range), enabled, staleTime})),
    combine: results => results.flatMap(result => result.data || [])
  });

  const prestations = useQueries({
    queries: ranges.map(range => ({...apiGetPlanningPrestations(range), enabled, staleTime})),
    combine: results => results.flatMap(result => result.data || [])
  });

  const index = useMemo(() => buildIndex(events, prestations), [events, prestations]);

  return {events, prestations, index};
};

/**
 * Events and prestations of the given months ("YYYY-MM"), month by month (see usePlanningRanges).
 */
export const usePlanningMonths = (months, enabled) => usePlanningRanges(
  useMemo(() => months.map(monthRange), [months]),
  enabled
);
